import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Calculator, Plus, Loader2, AlertTriangle, CheckCircle2, FileDown, Trash2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = { brouillon: 'bg-amber-100 text-amber-700', validee: 'bg-blue-100 text-blue-700', declaree: 'bg-green-100 text-green-700' };
const STATUS_LABELS = { brouillon: 'Brouillon', validee: 'Validée', declaree: 'Déclarée' };

export default function TVA() {
  const [declarations, setDeclarations] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ periode_type: 'mensuelle', periode_debut: '', periode_fin: '' });

  const fetchAll = async () => {
    try {
      const [dRes, aRes] = await Promise.all([
        axios.get(`${API_URL}/tva/declarations`),
        axios.get(`${API_URL}/tva/alerts`),
      ]);
      setDeclarations(dRes.data); setAlerts(aRes.data);
    } catch (e) {} finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleCreate = async () => {
    if (!form.periode_debut || !form.periode_fin) { toast.error('Dates requises'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/tva/declarations`, form);
      toast.success('Déclaration créée et calculée');
      setCreateOpen(false);
      setDetail(res.data);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleValidate = async (id) => {
    try { await axios.post(`${API_URL}/tva/declarations/${id}/validate`); toast.success('Déclaration validée'); fetchAll(); setDetail(null); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const handleDeclare = async (id) => {
    try { await axios.post(`${API_URL}/tva/declarations/${id}/declare`); toast.success('Déclaration marquée comme déclarée'); fetchAll(); setDetail(null); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };
  const handleDelete = async (id) => {
    if (!confirm('Supprimer cette déclaration ?')) return;
    try { await axios.delete(`${API_URL}/tva/declarations/${id}`); toast.success('Supprimée'); fetchAll(); setDetail(null); } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const openDetail = async (id) => {
    try { const res = await axios.get(`${API_URL}/tva/declarations/${id}`); setDetail(res.data); } catch (e) {}
  };

  const setMonthPeriod = () => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
    setForm({ ...form, periode_debut: lastMonth.toISOString().slice(0, 10), periode_fin: lastDay.toISOString().slice(0, 10), periode_type: 'mensuelle' });
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold"><Calculator className="w-6 h-6 inline mr-2" />TVA</h1>
          <p className="text-slate-500">Calcul automatique et déclarations</p>
        </div>
        <Button onClick={() => { setMonthPeriod(); setCreateOpen(true); }} className="bg-[#0F5156] hover:bg-[#0d464a]">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle déclaration
        </Button>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <Card key={i} className={`border-l-4 ${a.severity === 'critical' ? 'border-l-red-500 bg-red-50' : a.severity === 'warning' ? 'border-l-amber-500 bg-amber-50' : 'border-l-blue-500 bg-blue-50'}`}>
              <CardContent className="py-3 flex items-center gap-3">
                <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${a.severity === 'critical' ? 'text-red-500' : a.severity === 'warning' ? 'text-amber-500' : 'text-blue-500'}`} />
                <div>
                  <p className="text-sm font-medium">{a.message}</p>
                  {a.date_limite && <p className="text-xs text-slate-500">Date limite : {a.date_limite}</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Declarations list */}
      <Card>
        <CardHeader><CardTitle>Déclarations TVA</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Période</TableHead><TableHead>Type</TableHead><TableHead className="text-right">TVA collectée</TableHead><TableHead className="text-right">TVA déductible</TableHead><TableHead className="text-right">À payer / Crédit</TableHead><TableHead>Date limite</TableHead><TableHead>Statut</TableHead><TableHead className="w-20"></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {declarations.map(d => (
                <TableRow key={d.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openDetail(d)}>
                  <TableCell className="font-medium">{d.periode_debut} → {d.periode_fin}</TableCell>
                  <TableCell className="text-sm capitalize">{d.periode_type}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(d.total_tva_collectee)}</TableCell>
                  <TableCell className="text-right font-mono">{formatCurrency(d.total_tva_deductible)}</TableCell>
                  <TableCell className={`text-right font-mono font-semibold ${d.tva_a_payer > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {d.tva_a_payer > 0 ? formatCurrency(d.tva_a_payer) : `-${formatCurrency(d.credit_tva)}`}
                  </TableCell>
                  <TableCell className="text-sm">{d.date_limite || '-'}</TableCell>
                  <TableCell><Badge className={`${STATUS_COLORS[d.statut]} text-xs`}>{STATUS_LABELS[d.statut]}</Badge></TableCell>
                  <TableCell>{d.statut === 'brouillon' && <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(d.id); }}><Trash2 className="w-3 h-3 text-red-500" /></Button>}</TableCell>
                </TableRow>
              ))}
              {declarations.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500">Aucune déclaration</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail */}
      {detail && (
        <Card className="border-[#0F5156]">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Déclaration {detail.periode_debut} → {detail.periode_fin}</CardTitle>
              <div className="flex gap-2">
                {detail.statut === 'brouillon' && <Button variant="outline" onClick={() => handleValidate(detail.id)}>Valider</Button>}
                {detail.statut === 'validee' && <Button onClick={() => handleDeclare(detail.id)} className="bg-[#0F5156] hover:bg-[#0d464a]">Marquer déclarée</Button>}
                <Button variant="ghost" onClick={() => setDetail(null)}>Fermer</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold mb-2">TVA collectée (ventes)</h3>
                <Table>
                  <TableBody>
                    {(detail.tva_collectee_lignes || []).map((l, i) => (
                      <TableRow key={i}><TableCell className="text-sm">{l.label}</TableCell><TableCell className="text-right font-mono text-sm">Base : {formatCurrency(l.base_ht)}</TableCell><TableCell className="text-right font-mono font-medium">{formatCurrency(l.tva)}</TableCell></TableRow>
                    ))}
                    <TableRow className="font-bold"><TableCell>Total collectée</TableCell><TableCell></TableCell><TableCell className="text-right font-mono">{formatCurrency(detail.total_tva_collectee)}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </div>
              <div>
                <h3 className="font-semibold mb-2">TVA déductible (achats)</h3>
                <Table>
                  <TableBody>
                    {(detail.tva_deductible_lignes || []).map((l, i) => (
                      <TableRow key={i}><TableCell className="text-sm">{l.label}</TableCell><TableCell className="text-right font-mono text-sm">Base : {formatCurrency(l.base_ht)}</TableCell><TableCell className="text-right font-mono font-medium">{formatCurrency(l.tva)}</TableCell></TableRow>
                    ))}
                    <TableRow className="font-bold"><TableCell>Total déductible</TableCell><TableCell></TableCell><TableCell className="text-right font-mono">{formatCurrency(detail.total_tva_deductible)}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
            <Separator />
            <div className="flex justify-end gap-8 text-lg">
              <span>TVA nette : <strong className="font-mono">{formatCurrency(detail.tva_nette)}</strong></span>
              {detail.tva_a_payer > 0
                ? <span className="text-red-600 font-bold">À payer : {formatCurrency(detail.tva_a_payer)}</span>
                : <span className="text-green-600 font-bold">Crédit de TVA : {formatCurrency(detail.credit_tva)}</span>
              }
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvelle déclaration TVA</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Type de période</Label>
              <Select value={form.periode_type} onValueChange={v => setForm({...form, periode_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="mensuelle">Mensuelle</SelectItem><SelectItem value="trimestrielle">Trimestrielle</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Début de période</Label><Input type="date" value={form.periode_debut} onChange={e => setForm({...form, periode_debut: e.target.value})} /></div>
              <div><Label>Fin de période</Label><Input type="date" value={form.periode_fin} onChange={e => setForm({...form, periode_fin: e.target.value})} /></div>
            </div>
            <p className="text-sm text-slate-500">Le calcul sera effectué automatiquement à partir des factures clients (TVA collectée) et fournisseurs (TVA déductible).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Calculer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
